import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, BarChart3, Download, ArrowRight, User, Mail, CheckCircle, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const TRAITS = [
  { name: "Openness", color: "#8b5cf6", description: "Creativity & curiosity" },
  { name: "Conscientiousness", color: "#10b981", description: "Organization & discipline" },
  { name: "Extraversion", color: "#f59e0b", description: "Sociability & energy" },
  { name: "Agreeableness", color: "#ec4899", description: "Cooperation & trust" },
  { name: "Neuroticism", color: "#64748b", description: "Emotional patterns" },
];

const FEATURES = [
  {
    icon: Brain,
    title: "Know Yourself Better",
    description: "Gain deep insights into what makes you uniquely you. Understand your natural tendencies, preferences, and strengths.",
  },
  {
    icon: Heart,
    title: "Show Up Authentically",
    description: "When you understand yourself, you can be genuine in every interaction. No masks, no pretending — just the real you.",
  },
  {
    icon: Sparkles,
    title: "Deeper Connections",
    description: "There's no 'right' personality. Learn to appreciate your traits and use them as your authentic superpower.",
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    
    if (!formData.email.trim() || !formData.email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    
    setIsSubmitting(true);
    sessionStorage.setItem("userInfo", JSON.stringify(formData));
    
    setTimeout(() => {
      navigate("/test");
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 py-4">
        <div className="container mx-auto px-6 flex items-center justify-between">
          <a href="https://integritygo.com/" target="_blank" rel="noopener noreferrer">
            <img 
              src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/wzCO7KFXOfMKBfoO2dpF/media/6774787e91c39998b2e142f0.jpeg" 
              alt="IntegrityGO" 
              className="h-12 md:h-14"
            />
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 bg-sky-100 text-sky-800 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                <Brain className="w-4 h-4" />
                Personality Assessment
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                <span className="block text-sky-500">
                  You Are Already DOPE!
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
                Discover your unique personality traits and learn how to bring your authentic self 
                to every conversation and relationship. Because the best version of you is the real you.
              </p>

              {!showForm ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button
                    data-testid="get-started-btn"
                    onClick={() => setShowForm(true)}
                    className="bg-sky-500 text-white hover:bg-sky-600 px-10 py-6 rounded-full text-lg font-semibold shadow-lg shadow-sky-500/30 hover:scale-105 active:scale-95 transition-transform"
                  >
                    Discover Your Personality
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <p className="mt-4 text-sm text-gray-500">120 questions • Takes ~15 minutes</p>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl p-8 max-w-md mx-auto shadow-xl border border-gray-100"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">
                    Enter your details to begin
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2 text-left">
                      <Label htmlFor="name" className="text-gray-700 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Your Name
                      </Label>
                      <Input
                        id="name"
                        data-testid="user-name-input"
                        type="text"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-gray-50 border-gray-200 focus:border-sky-500 focus:ring-sky-500/20"
                      />
                    </div>
                    
                    <div className="space-y-2 text-left">
                      <Label htmlFor="email" className="text-gray-700 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email Address
                      </Label>
                      <Input
                        id="email"
                        data-testid="user-email-input"
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-gray-50 border-gray-200 focus:border-sky-500 focus:ring-sky-500/20"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      data-testid="start-test-btn"
                      disabled={isSubmitting}
                      className="w-full bg-sky-500 text-white hover:bg-sky-600 py-6 rounded-xl font-semibold hover:scale-[1.02] active:scale-[0.98] transition-transform"
                    >
                      {isSubmitting ? "Starting..." : "Start Assessment"}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Traits Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Big Five Personality Traits
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Five dimensions that make up who you are
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {TRAITS.map((trait, index) => (
              <motion.div
                key={trait.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="text-center p-5 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all h-full">
                  <CardContent className="p-0">
                    <div
                      className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center"
                      style={{ backgroundColor: `${trait.color}20` }}
                    >
                      <div
                        className="w-5 h-5 rounded-full"
                        style={{ backgroundColor: trait.color }}
                      />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{trait.name}</h3>
                    <p className="text-xs text-gray-500">{trait.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Understanding Yourself Matters
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Self-awareness is the foundation of authentic connection
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-8 h-full bg-white border-gray-100 hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="w-14 h-14 rounded-2xl bg-sky-500 flex items-center justify-center mb-6">
                      <feature.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Get */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What You'll Receive
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                "Detailed scores across 5 personality dimensions",
                "Personalized interpretation of your results",
                "Understanding of your natural tendencies",
                "Shareable results with friends and colleagues",
                "Insights into how you relate to others",
                "A foundation for showing up as your true self"
              ].map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                >
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Know Yourself Better?
            </h2>
            <p className="text-lg text-gray-400 mb-10 max-w-xl mx-auto">
              Take the assessment now. No judgment. Just honest insights into who you really are.
            </p>
            <Button
              data-testid="cta-start-btn"
              onClick={() => {
                setShowForm(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="bg-sky-500 text-white hover:bg-sky-600 px-10 py-6 rounded-full text-lg font-semibold hover:scale-105 active:scale-95 transition-transform"
            >
              Start Assessment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/wzCO7KFXOfMKBfoO2dpF/media/6774787e91c39998b2e142f0.jpeg" 
              alt="IntegrityGO" 
              className="h-10"
            />
            <p className="text-gray-500 text-sm text-center">
              © Copyright 2026 IntegrityGO. All rights reserved.
            </p>
            <a 
              href="https://integritygo.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              integritygo.com
            </a>
            <button
              onClick={() => navigate("/admin")}
              className="text-xs text-gray-600 hover:text-gray-400 transition-colors"
              data-testid="admin-link-btn"
            >
              Admin
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
